// src/api/eventsApi.ts

import { baseApi } from './baseApi';
import type {
  Event,
  EventKind,
  EventCategory,
  CreateEventRequest,
  UpdateEventRequest,
} from '../types/api';

export interface EventAttendanceProjection {
  provenance: 'provider_observed';
  participants: { id: string; displayName: string | null; joinedAt: string | null; leftAt: string | null }[];
  observations: {
    id: string;
    source: string;
    observedAt: string;
    startedAt: string | null;
    endedAt: string | null;
    resolutionState: string;
    resolvedContactId: string | null;
    resolvedUserId: string | null;
    resolvedEventAttendeeId: string | null;
  }[];
  summary: { sessionCount: number; observationCount: number; resolvedCount: number; unresolvedCount: number };
}

export interface EventArtifactsResponse {
  artifacts: {
    id: string;
    artifactType: 'recording' | 'transcript';
    mimeType: string | null;
    status: string;
    startedAt: string | null;
    endedAt: string | null;
    provenance: string;
    fileType: string | null;
    fileSize: number | null;
  }[];
  summary: { hasRecording: boolean; hasTranscript: boolean; count: number };
}

interface GetEventsParams {
  workspaceId?: string;
  kind?: EventKind;
  category?: EventCategory;
  from?: string;
  to?: string;
}

export const eventsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEvents: builder.query<Event[], GetEventsParams>({
      query: (params) => ({
        url: '/events',
        params: {
          ...(params.workspaceId && { workspaceId: params.workspaceId }),
          ...(params.kind && { kind: params.kind }),
          ...(params.category && { category: params.category }),
          ...(params.from && { from: params.from }),
          ...(params.to && { to: params.to }),
        },
      }),
      providesTags: (result, error, { workspaceId, kind }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Event' as const, id })),
              { type: 'Event', id: kind || workspaceId || 'LIST' },
            ]
          : [{ type: 'Event', id: kind || workspaceId || 'LIST' }],
    }),

    getEvent: builder.query<Event, string>({
      query: (id) => `/events/${id}`,
      providesTags: (result, error, id) => [{ type: 'Event', id }],
    }),

    // C2 — provider-OBSERVED attendance projection (distinct from RSVP/EventAttendee).
    getEventAttendance: builder.query<EventAttendanceProjection, string>({
      query: (id) => `/events/${id}/attendance`,
      providesTags: (result, error, id) => [{ type: 'Event', id: `attendance-${id}` }],
    }),

    // C2 — recording/transcript artifact METADATA (no media/text/URL).
    getEventArtifacts: builder.query<EventArtifactsResponse, string>({
      query: (id) => `/events/${id}/artifacts`,
      providesTags: (result, error, id) => [{ type: 'Event', id: `artifacts-${id}` }],
    }),

    createEvent: builder.mutation<Event, CreateEventRequest>({
      query: (body) => ({
        url: '/events',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { workspaceId, kind }) => [
        { type: 'Event', id: kind || workspaceId || 'LIST' },
        { type: 'Event', id: 'LIST' },
      ],
    }),

    updateEvent: builder.mutation<Event, { id: string } & UpdateEventRequest>({
      query: ({ id, ...body }) => ({
        url: `/events/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Event', id },
        { type: 'Event', id: 'LIST' },
      ],
    }),

    deleteEvent: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/events/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Event', id },
        { type: 'Event', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useGetEventQuery,
  useGetEventAttendanceQuery,
  useGetEventArtifactsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
} = eventsApi;
